/**
 * A job order's nine steps, as the factory counts them and TAP reads them.
 *
 * ── One vocabulary, two levels of detail ───────────────────────────────────
 *
 * TAP tracks eight STATUSES on cg_orders. El Salvador described nine STEPS.
 * They are not two models — the nine steps fit inside the eight statuses, and
 * two of those statuses hold two steps each:
 *
 *   PO Sent to ES   →  Receiving the PO   ·  Acknowledged
 *   Sampling        →  Making samples     ·  Samples submitted
 *   In Production   →  Building           ·  Marked ready
 *
 * That is why nothing here adds a status. The finer steps are DERIVED from
 * facts already on the row — supplier_confirmed_at, whether a sample photo
 * exists, supplier_ready_for_shipment. Inventing statuses for them would
 * double TAP's board to describe work TAP does not do, and give the two sides
 * two vocabularies that can disagree.
 *
 * ── Milestones and dwell states are different ──────────────────────────────
 *
 * Some steps are places a job SITS in (making samples, in production). Others
 * are instants it passes THROUGH — accepting the PO moves the order to
 * Sampling in the same transaction, so "Acknowledged" is never a current
 * state, only a completed one. A progress bar that looks for a job resting on
 * an instant would show nothing as current at exactly the moments something
 * just happened.
 */

/**
 * The three fields the steps are derived from.
 *
 * Narrower than CgOrder on purpose. This function is called from the supplier
 * portal, whose payload is an allow-list and deliberately is NOT a cg_orders
 * row — demanding the whole row would have forced a cast at that call site,
 * and a cast is how a shape mismatch becomes a runtime surprise rather than a
 * compile error. Anything holding these three can ask the question.
 */
export interface JobStageInput {
  status: string;
  supplier_confirmed_at: string | null;
  supplier_ready_for_shipment: boolean;
}

export type JobStepKey =
  | 'po_received'
  | 'po_acknowledged'
  | 'making_samples'
  | 'samples_submitted'
  | 'awaiting_approval'
  | 'samples_approved'
  | 'in_production'
  | 'shipped'
  | 'delivered';

export interface JobStep {
  key: JobStepKey;
  /** What the factory calls it. TAP sees the same words. */
  label: string;
  state: 'done' | 'current' | 'ahead';
  /** Whose move it is while this step is current. */
  turn: 'supplier' | 'tap' | null;
}

/** The facts the nine steps are derived from, beyond the order row itself. */
export interface JobFacts {
  /** Whether any sample photo has been uploaded for this job. */
  sampleSubmitted: boolean;
}

const LABELS: Record<JobStepKey, string> = {
  po_received: 'PO received',
  po_acknowledged: 'PO acknowledged',
  making_samples: 'Making samples',
  samples_submitted: 'Samples submitted',
  awaiting_approval: 'Awaiting TAP approval',
  samples_approved: 'Samples approved',
  in_production: 'In production',
  shipped: 'Shipped',
  delivered: 'Delivered',
};

/**
 * How far along the statuses this job has travelled.
 *
 * Ranked rather than compared by name so "is it at or past In Production" is
 * one question with one answer. An unrecognised status ranks 0 — a job whose
 * status somebody typed by hand should read as "not started" rather than
 * silently satisfying every "at or past" test in this file.
 */
const RANK: Record<string, number> = {
  'Order Placed': 0,
  // Same rank as Order Placed, deliberately. The client signing off is a
  // milestone for TAP and changes nothing about the job: the PO is still the
  // commitment, and until it is issued the factory has no work and should not
  // see the order. Ranking it above 0 would make visibleToSupplier true for an
  // order nobody has been asked to build.
  'Client Approved': 0,
  'PO Sent to ES': 1,
  Sampling: 2,
  'Sample Approval': 3,
  'In Production': 4,
  Shipped: 5,
  Delivered: 6,
  Closed: 7,
};

function rank(status: string): number {
  return RANK[status] ?? 0;
}

/**
 * The nine steps, in the order they happen.
 *
 * Exported because the supplier's board columns are these same nine, and a
 * board that keeps its own copy of the list is a board that drifts out of step
 * with the bar inside each job the first time one of them changes.
 */
export const JOB_STEP_ORDER: JobStepKey[] = [
  'po_received',
  'po_acknowledged',
  'making_samples',
  'samples_submitted',
  'awaiting_approval',
  'samples_approved',
  'in_production',
  'shipped',
  'delivered',
];

/**
 * The nine steps for one job.
 *
 * `current` is the FIRST step that is not done. Everything before it is done,
 * everything after is ahead — so a bar can never show two current steps, and
 * never none while the job is live.
 *
 * A closed or delivered job has no current step at all, which is correct: the
 * work is finished and pointing at a step would imply somebody owes something.
 */
export function jobSteps(order: JobStageInput, facts: JobFacts): JobStep[] {
  const at = rank(order.status);
  const confirmed = order.supplier_confirmed_at !== null;
  const ready = order.supplier_ready_for_shipment === true;

  // Done-ness per step, in order. Each is a fact about the row, not a guess
  // from the step before it — so a job that skipped a step (an admin moving a
  // status by hand) still renders truthfully rather than claiming everything
  // behind it happened.
  const done: Record<JobStepKey, boolean> = {
    // Receiving is where the job SITS until the factory accepts it — not an
    // instant that passes the moment the PO is sent.
    //
    // It read the other way at first: po_received went done as soon as the
    // status reached 'PO Sent to ES', which made "PO acknowledged" the current
    // step on a job nobody had acknowledged. A factory looking at their own
    // board saw themselves credited with a decision they had not made.
    //
    // Acknowledged is the milestone here, and milestones are only ever done —
    // accepting moves the status to Sampling in the same transaction, so
    // nothing ever rests on it.
    po_received: confirmed || at >= 2,
    po_acknowledged: confirmed || at >= 2,
    // Making samples ends when the first photo lands, which is also what moves
    // the status. Either fact alone is enough.
    making_samples: facts.sampleSubmitted || at >= 3,
    samples_submitted: facts.sampleSubmitted || at >= 3,
    awaiting_approval: at >= 4,
    samples_approved: at >= 4,
    // Production ends when the factory raises the ready flag — their part
    // finishes before the stage does.
    in_production: ready || at >= 5,
    shipped: at >= 5,
    delivered: at >= 6,
  };

  const firstOpen = JOB_STEP_ORDER.find((key) => !done[key]);

  return JOB_STEP_ORDER.map((key) => ({
    key,
    label: LABELS[key],
    state: done[key] ? 'done' : key === firstOpen ? 'current' : 'ahead',
    turn: done[key] || key !== firstOpen ? null : TURN[key],
  }));
}

/**
 * Whose move it is while a step is current.
 *
 * Written from neither side's point of view — 'supplier' and 'tap' rather than
 * 'you' and 'them', so the same array renders on both screens and each side
 * decides which word to print. The supplier portal saying "you" and the CG
 * board saying "you" about the same step is how two views of one job start
 * contradicting each other.
 */
const TURN: Record<JobStepKey, 'supplier' | 'tap'> = {
  po_received: 'supplier',
  po_acknowledged: 'supplier',
  making_samples: 'supplier',
  samples_submitted: 'supplier',
  awaiting_approval: 'tap',
  samples_approved: 'tap',
  in_production: 'supplier',
  shipped: 'tap',
  delivered: 'tap',
};

/** The step a job is sitting on, or null when it is finished. */
export function currentStep(steps: JobStep[]): JobStep | null {
  return steps.find((step) => step.state === 'current') ?? null;
}

/**
 * Whether the factory should be able to see this job at all.
 *
 * An order still in 'Order Placed' is with the client and the commercial team.
 * The PO is the commitment; before it is issued there is no job — and showing
 * a factory an order that may never be placed creates an expectation TAP has
 * not made, or worse invites somebody to start work on a design the client has
 * not approved.
 */
export function visibleToSupplier(order: Pick<JobStageInput, 'status'>): boolean {
  return rank(order.status) >= 1;
}
